'use client';

import { apiClient } from '@/sdk/shared/apiClient';
import type { GeneralSettings } from './types';
import { getApiBaseUrl } from '@/lib/env';

// ─── Backend Raw Types (snake_case) ─────────────────────────────────────────

interface BackendGeneralSettings {
  id: string | null;
  tenant_id: string;
  logo_url: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface BackendResponse {
  message: string;
  data: BackendGeneralSettings;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function mapBackendGeneralSettings(raw: any): GeneralSettings {
  let logoUrl = raw.logo_url || raw.logoUrl;
  if (logoUrl && logoUrl.startsWith('/api/')) {
    const apiBaseUrl = getApiBaseUrl();
    if (apiBaseUrl && apiBaseUrl !== '/api') {
      logoUrl = `${apiBaseUrl.replace(/\/api$/, '')}${logoUrl}`;
    }
  }
  return {
    id: raw.id,
    tenantId: raw.tenant_id || raw.tenantId,
    logoUrl: logoUrl,
    employeeIdPrefix: raw.employee_id_prefix || raw.employeeIdPrefix || 'LGB',
    createdAt: raw.created_at || raw.createdAt,
    updatedAt: raw.updated_at || raw.updatedAt,
  };
}

// ─── Module Level Cache ──────────────────────────────────────────────────────

const cachedSettingsMap = new Map<string, GeneralSettings>();
const activePromiseMap = new Map<string, Promise<GeneralSettings>>();

// ─── API Functions ───────────────────────────────────────────────────────────

export async function getGeneralSettings(tenantSlug?: string, forceRefetch = false): Promise<GeneralSettings> {
  const token = apiClient.getToken();

  // If we don't have a token and don't have a tenantSlug, we cannot fetch settings.
  // Return default settings directly to avoid unnecessary 400 Bad Request calls.
  if (!token && !tenantSlug) {
    return {
      id: null,
      tenantId: '',
      logoUrl: null,
      employeeIdPrefix: 'LGB',
      createdAt: null,
      updatedAt: null,
    };
  }

  const cacheKey = tenantSlug || '__authenticated__';

  if (cachedSettingsMap.has(cacheKey) && !forceRefetch) {
    return cachedSettingsMap.get(cacheKey)!;
  }
  if (activePromiseMap.has(cacheKey) && !forceRefetch) {
    return activePromiseMap.get(cacheKey)!;
  }

  const query = tenantSlug ? `?tenantSlug=${encodeURIComponent(tenantSlug)}` : '';
  const endpoint = `/admin/general-settings${query}`;

  const promise = apiClient.get<BackendResponse>(endpoint)
    .then((response) => {
      const mapped = mapBackendGeneralSettings(response.data);
      cachedSettingsMap.set(cacheKey, mapped);
      activePromiseMap.delete(cacheKey);
      return mapped;
    })
    .catch((err) => {
      activePromiseMap.delete(cacheKey);
      throw err;
    });

  activePromiseMap.set(cacheKey, promise);
  return promise;
}

export async function updateGeneralSettings(
  tenantSlug: string, 
  file?: File | null, 
  employeeIdPrefix?: string
): Promise<GeneralSettings> {
  const formData = new FormData();
  if (file) {
    formData.append('file', file);
  }
  if (employeeIdPrefix !== undefined) {
    formData.append('employee_id_prefix', employeeIdPrefix);
  }
  formData.append('tenantSlug', tenantSlug);

  const token = apiClient.getToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const baseUrl = getApiBaseUrl();
  const endpoint = `${baseUrl}/admin/general-settings`;

  const response = await fetch(endpoint, {
    method: 'PUT',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to update settings' }));
    throw new Error(error.error || 'Failed to update settings');
  }

  const result: BackendResponse = await response.json();
  const mapped = mapBackendGeneralSettings(result.data);
  
  // Update in-memory cache directly with new settings for both keys
  cachedSettingsMap.set('__authenticated__', mapped);
  cachedSettingsMap.set(tenantSlug, mapped);

  return mapped;
}

export const adminGeneralSettingsApi = {
  getGeneralSettings,
  updateGeneralSettings,
};

export default adminGeneralSettingsApi;
